import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, Plus } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const STATUS_STYLE = {
  calcolata: "bg-slate-100 text-slate-600", parziale: "bg-amber-100 text-amber-800",
  incassata: "bg-emerald-100 text-emerald-700", esente: "bg-sky-100 text-sky-700", anomalia: "bg-rose-100 text-rose-700",
};
const TYPES = [
  { v: "incasso", l: "Incasso totale" }, { v: "incasso_parziale", l: "Incasso parziale" },
  { v: "rettifica", l: "Rettifica" }, { v: "annullamento", l: "Annullamento" }, { v: "rimborso", l: "Rimborso" },
];
const METHODS = ["contanti", "carta", "bonifico", "pos", "altro"];

export function CityTaxCollectPanel({ bookingId, canWrite, onChange }) {
  const [pos, setPos] = useState(null);
  const [openDlg, setOpenDlg] = useState(false);
  const [form, setForm] = useState({ type: "incasso", amount: "", method: "contanti", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!bookingId) return;
    api.get(`/city-tax/position-by-booking/${bookingId}`).then((r) => setPos(r.data.position)).catch(() => setPos(null));
  }, [bookingId]);
  useEffect(() => { load(); }, [load]);

  if (!pos) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const needsAmount = ["incasso", "incasso_parziale", "rettifica", "rimborso"].includes(form.type);
    const requiresNote = ["rettifica", "annullamento", "rimborso"].includes(form.type);
    if (needsAmount && !form.amount) { toast.error("Inserisci un importo"); return; }
    if (requiresNote && !form.note.trim()) { toast.error("La motivazione è obbligatoria per questo tipo di movimento"); return; }
    setSaving(true);
    try {
      await api.post(`/city-tax/positions/${pos.id}/collect`, {
        type: form.type, amount: Number(form.amount) || 0, method: form.method, note: form.note,
      });
      toast.success("Movimento registrato");
      setOpenDlg(false); setForm({ type: "incasso", amount: "", method: "contanti", note: "" });
      load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const needsNote = ["rettifica", "annullamento", "rimborso"].includes(form.type);
  const needsAmount = ["incasso", "incasso_parziale", "rettifica", "rimborso"].includes(form.type);

  return (
    <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-3" data-testid="citytax-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Landmark size={16} className="text-indigo-600" />
          <span className="text-sm font-semibold text-slate-800">Tassa di soggiorno</span>
          <Badge className={STATUS_STYLE[pos.status] || "bg-slate-100"} data-testid="citytax-status">{pos.status}</Badge>
        </div>
        {canWrite && pos.status !== "esente" && !pos.locked && (
          <Button size="sm" className="h-8 gap-1.5 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="citytax-collect-btn" onClick={() => setOpenDlg(true)}>
            <Plus size={14} /> Registra incasso
          </Button>
        )}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-center text-sm">
        <div><p className="text-xs text-slate-400">Dovuta</p><p className="font-semibold">{eur(pos.amount_due)}</p></div>
        <div><p className="text-xs text-slate-400">Incassata</p><p className="font-semibold text-emerald-700">{eur(pos.amount_collected)}</p></div>
        <div><p className="text-xs text-slate-400">Da riscuotere</p><p className="font-semibold text-rose-600">{eur(pos.balance)}</p></div>
      </div>

      <Dialog open={openDlg} onOpenChange={setOpenDlg}>
        <DialogContent data-testid="citytax-collect-dialog">
          <DialogHeader>
            <DialogTitle>Registra movimento tassa di soggiorno</DialogTitle>
            <DialogDescription>Incasso totale/parziale, rettifica, annullamento o rimborso. Ogni movimento è tracciato nell'Audit Log e nella Timeline.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Tipo movimento</label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="citytax-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.type === "annullamento" && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">Storna l'intero incassato attuale ({eur(pos.amount_collected)}). Il movimento resta nello storico.</p>
            )}
            {needsAmount && (
              <div>
                <label className="text-xs text-slate-500">Importo (€){form.type === "rettifica" ? " — usa valore negativo per storni" : ""}</label>
                <Input data-testid="citytax-amount" type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" />
              </div>
            )}
            {form.type !== "annullamento" && (
              <div>
                <label className="text-xs text-slate-500">Metodo di pagamento</label>
                <Select value={form.method} onValueChange={(v) => set("method", v)}>
                  <SelectTrigger data-testid="citytax-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500">Nota{needsNote ? " (obbligatoria)" : ""}</label>
              <Textarea data-testid="citytax-note" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} placeholder={needsNote ? "Motivazione…" : "Facoltativa"} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDlg(false)}>Annulla</Button>
            <Button data-testid="citytax-save-btn" onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? "Salvataggio…" : "Registra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CityTaxCollectPanel;
